/**
 * apps/web :: pass :: screens/PassStart
 *
 * The buy flow for the SP2 vacationer "traveler pass" (ROADS-569 W1.B).
 * The traveler picks a single country pack (Italy / Switzerland / France)
 * or a regional / all-access bundle, confirms, and we hand off to the
 * SHARED checkout: `navigate('/checkout?tier=tourist_out&country=XX')`
 * (ROADS-567). CheckoutPage — the same page every paid tier uses — owns
 * the Stripe redirect and its error states; the pass is not a bespoke
 * direct-to-Stripe path.
 *
 * Beta scope: the regional / all-access option is a thin UX affordance —
 * we still route to a SINGLE-country checkout for the country the user
 * highlighted (or the first pack for "all access"). Fanning a bundle out
 * to N entitlements is a webhook concern, not a client concern.
 *   // TODO bundle = N rows via webhook loop
 *
 * Wrapped in PassLayout for consistent pass chrome.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import PassLayout from "../PassLayout";
import { listPacks, type TravelerPack } from "@/lib/pass-content";

/** Synthetic option id for the regional / all-access bundle choice. */
const BUNDLE_OPTION_ID = "__bundle__";

export default function PassStart(): JSX.Element {
  const packs = listPacks();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);

  /**
   * Resolve the country_code we actually charge for. A single-country
   * pick maps 1:1 to its `jurisdiction.id` (IT / CH / FR). The bundle
   * option, for the beta, charges for the first available pack — the
   * webhook will eventually grant the remaining rows.
   */
  function resolveCountryCode(): string | null {
    if (!selected) return null;
    if (selected === BUNDLE_OPTION_ID) {
      const first = packs[0];
      return first ? first.jurisdiction.id : null;
    }
    return selected;
  }

  /**
   * Hand off to the SHARED checkout (ROADS-567) — the same
   * `/checkout?tier=...` flow every paid tier uses. CheckoutPage reads
   * the prefilled country and owns the Stripe redirect + its error
   * states; the pass is no longer a bespoke direct-to-Stripe path.
   */
  function handleConfirm(): void {
    const countryCode = resolveCountryCode();
    if (!countryCode) return;
    const search = new URLSearchParams({
      tier: "tourist_out",
      country: countryCode,
    });
    navigate(`/checkout?${search.toString()}`);
  }

  return (
    <PassLayout title="Get your travel pass">
      <p className="text-sm text-neutral-700">
        Pick where you&apos;re driving. Your pass unlocks the local rules,
        a trip plan, quick lessons, and an offline emergency card.
      </p>

      <fieldset
        className="mt-md flex flex-col gap-2"
        data-testid="pass-start-options"
      >
        <legend className="sr-only">Choose a destination</legend>

        {packs.map((pack: TravelerPack) => {
          const code = pack.jurisdiction.id;
          const active = selected === code;
          return (
            <label
              key={code}
              data-testid={`pass-start-option-${code}`}
              className={`flex cursor-pointer items-center justify-between rounded-md border px-md py-sm text-sm ${
                active
                  ? "border-indigo-500 bg-indigo-50 font-semibold text-indigo-700"
                  : "border-neutral-200 text-neutral-800 hover:border-indigo-300"
              }`}
            >
              <span>{pack.jurisdiction.name}</span>
              <input
                type="radio"
                name="pass-destination"
                value={code}
                checked={active}
                onChange={() => setSelected(code)}
                className="sr-only"
              />
              <span className="text-xs text-neutral-500">$15</span>
            </label>
          );
        })}

        {/* Regional / all-access bundle — beta: single-country checkout. */}
        <label
          data-testid={`pass-start-option-bundle`}
          className={`flex cursor-pointer items-center justify-between rounded-md border px-md py-sm text-sm ${
            selected === BUNDLE_OPTION_ID
              ? "border-indigo-500 bg-indigo-50 font-semibold text-indigo-700"
              : "border-neutral-200 text-neutral-800 hover:border-indigo-300"
          }`}
        >
          <span>All-access (Europe)</span>
          <input
            type="radio"
            name="pass-destination"
            value={BUNDLE_OPTION_ID}
            checked={selected === BUNDLE_OPTION_ID}
            onChange={() => setSelected(BUNDLE_OPTION_ID)}
            className="sr-only"
          />
          <span className="text-xs text-neutral-500">Beta</span>
        </label>
      </fieldset>

      <button
        type="button"
        data-testid="pass-start-confirm"
        disabled={!selected}
        onClick={handleConfirm}
        className="mt-lg inline-flex h-11 w-full items-center justify-center rounded-xl bg-indigo-600 px-md text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-neutral-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-700"
      >
        Continue to checkout
      </button>

      <p className="mt-sm text-xs text-neutral-500">
        Prices in USD. One-time purchase for your trip window.
      </p>
    </PassLayout>
  );
}
